import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";
import { ArrowRight, ChevronRight, Clock } from "lucide-react";
import { blogPosts } from "@/lib/blog-data";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Epoxy Flooring Guides & Insights | Blanco Pro Services NJ" },
      {
        name: "description",
        content:
          "Buyer's guides, installation insights, and spec resources for residential, commercial, and industrial epoxy flooring across New Jersey.",
      },
      { property: "og:title", content: "Epoxy Flooring Guides, Blanco Pro Services" },
      { property: "og:description", content: "In-depth guides for residential, commercial & industrial epoxy in NJ." },
      { property: "og:url", content: "https://blancopros.com/blog" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Epoxy Flooring Guides, Blanco Pro Services" },
      { name: "twitter:description", content: "In-depth guides for residential, commercial & industrial epoxy in NJ." },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/blog" }],
  }),
  component: BlogIndex,
});

const accent = {
  residential: "from-amber-400/30 to-amber-200/10",
  commercial: "from-sky-400/30 to-sky-200/10",
  industrial: "from-slate-400/30 to-slate-200/10",
} as const;

function BlogIndex() {
  return (
    <>
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-7xl mx-auto">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground">Guides</span>
          </nav>
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Knowledge Base</div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
              Epoxy flooring <span className="bg-gradient-gold bg-clip-text text-transparent">guides.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl">
              In-depth buyer's guides for every type of space we coat in New Jersey, from single-car garages to 100,000 sq ft manufacturing plants.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          {blogPosts.map((post, idx) => (
            <Reveal key={post.slug} delay={idx * 120}>
              <Link
                to="/blog/$slug"
                params={{ slug: post.slug }}
                className="group relative block h-full rounded-3xl border border-border bg-card p-8 shadow-soft hover:shadow-elegant transition-all overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${accent[post.category]} opacity-0 group-hover:opacity-100 transition-opacity`} />
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">{post.heroTagline}</div>
                  <h2 className="font-display text-2xl font-bold mb-4 leading-tight">{post.title}</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5" /> {post.readTime}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground group-hover:gap-2 transition-all">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
